/* BookingPage.jsx — gallery + staff (robust reviews loader)
   · Sequential fetch: try providerId; if empty → try provider name
   · Never sends providerId & provider together (keeps old rows visible)
   · Busts caches to ensure latest reviews show after submission
   · Touch swipe support on hero banners (mobile & desktop)
----------------------------------------------------------------- */
import React, { useState, useEffect, useLayoutEffect, useCallback, useMemo, useRef } from 'react';
import { createPortal } from 'react-dom';
import { useParams, useNavigate }     from 'react-router-dom';
import moment                         from 'moment';
import { FaMapMarkerAlt, FaPhoneAlt, FaStar, FaMap, FaImages } from 'react-icons/fa';
import './BookingPage.css';
import './BookingPageRedesign.css';
import { http }                       from '../lib/http';   // ← use wrapper (relative path, no alias)
import { useI18n }                    from '../contexts/I18n';
import { useAuth }                    from '../contexts/AuthContext';
import { img }                        from '../utils/img';  // ← use same image utility as barbershop
import { saveBookingIntent, createBookingIntent } from '../utils/bookingIntent';
import { markVerifyForBooking } from '../utils/emailVerification';
import {
  getFakeReviewsForShop,
  mergeShopReviews,
} from '../utils/fakeShopReviews';

/* ── helpers ─────────────────────────────────────────── */
const toNumber = (v = '') => Number(v.toString().replace(/[^\d.]+/g, ''));

const Stars = ({ n = 0 }) => {
  const out = [];
  for (let i = 1; i <= 5; i++) {
    const diff = n - i + 1;
    const cls =
      diff >= 1 ? 'filled' : diff >= 0.25 ? 'half' : '';
    out.push(
      <span key={i} className={`star ${cls}`.trim()}>
        ★
      </span>
    );
  }
  return <>{out}</>;
};

// robust staff accessor
const getStaffList = (shopObj) => {
  if (!shopObj) return [];
  if (Array.isArray(shopObj.staff) && shopObj.staff.length) return shopObj.staff;
  if (Array.isArray(shopObj.staffMembers) && shopObj.staffMembers.length) return shopObj.staffMembers;
  return [];
};

// Calendar constants
const WEEKDAYS = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'];
const MONTHS = moment.months();

/* ── component ───────────────────────────────────────── */
export default function BookingPage() {
  const { t, lang } = useI18n();
  const { shopName } = useParams();
  const navigate     = useNavigate();
  const { isAuthenticated, isLoading: authLoading } = useAuth();

  /* state */
  const [shop,     setShop]     = useState(null);
  const [selectedServices, setSelectedServices] = useState([]); // [{ serviceName, price }, ...]
  const [day,      setDay]      = useState('');
  const [slot,     setSlot]     = useState('');
  const [open,     setOpen]     = useState(false);   // reviews modal
  const [staff,    setStaff]    = useState(null);
  const [heroIdx,  setHeroIdx]  = useState(0);
  const [reviews,  setReviews]  = useState([]);
  const [loading,  setLoading]  = useState(true);
  const [bookedSlots, setBookedSlots] = useState([]);
  const [currentMonth, setCurrentMonth] = useState(moment());
  const [showPortfolio, setShowPortfolio] = useState(false);
  const [portfolioLightboxIdx, setPortfolioLightboxIdx] = useState(null);
  const [showAllServices, setShowAllServices] = useState(false);
  const [isMobileServices, setIsMobileServices] = useState(false);

  /* ── touch swipe state ────────────────────────────── */
  const touchStartX = useRef(null);
  const SWIPE_THRESHOLD = 40; // px

  const handleTouchStart = useCallback((e) => {
    touchStartX.current = e.touches[0].clientX;
  }, []);

  const handleTouchEnd = useCallback((e, totalImages) => {
    if (touchStartX.current === null) return;
    const delta = touchStartX.current - e.changedTouches[0].clientX;
    if (Math.abs(delta) < SWIPE_THRESHOLD) return;
    if (delta > 0) {
      // swiped left → next image
      setHeroIdx(prev => (prev + 1) % totalImages);
    } else {
      // swiped right → prev image
      setHeroIdx(prev => (prev - 1 + totalImages) % totalImages);
    }
    touchStartX.current = null;
  }, []);

  // Refs for scrolling to sections
  const portfolioRef = useRef(null);
  const staffRef = useRef(null);
  const serviceRef = useRef(null);
  const dateRef = useRef(null);
  const timeRef = useRef(null);
  const receiptRef = useRef(null);
  const reviewsModalRef = useRef(null);
  const reviewsOverlayRef = useRef(null);

  const scrollPageAndReviewsToTop = useCallback(() => {
    window.scrollTo(0, 0);
    document.documentElement.scrollTop = 0;
    document.body.scrollTop = 0;
    if (reviewsOverlayRef.current) reviewsOverlayRef.current.scrollTop = 0;
    if (reviewsModalRef.current) reviewsModalRef.current.scrollTop = 0;
  }, []);

  // Mobile-only: collapse services list to first 5
  useEffect(() => {
    const mq = window.matchMedia('(max-width: 768px)');
    const sync = () => {
      setIsMobileServices(mq.matches);
      if (!mq.matches) setShowAllServices(false);
    };
    sync();
    mq.addEventListener('change', sync);
    return () => mq.removeEventListener('change', sync);
  }, []);

  // Before paint: pin page + sheet to top whenever reviews open (fixes mid-list view)
  useLayoutEffect(() => {
    if (!open) return undefined;

    const isMobile = window.matchMedia('(max-width: 768px)').matches;
    const prevScrollY = window.scrollY || document.documentElement.scrollTop || 0;

    if (isMobile) {
      scrollPageAndReviewsToTop();
      document.documentElement.style.overflow = 'hidden';
      document.body.style.overflow = 'hidden';
    } else {
      document.body.style.position = 'fixed';
      document.body.style.top = `-${prevScrollY}px`;
      document.body.style.width = '100%';
      document.body.style.overflow = 'hidden';
    }

    const t1 = window.setTimeout(scrollPageAndReviewsToTop, 0);
    const t2 = window.setTimeout(scrollPageAndReviewsToTop, 50);
    const t3 = window.setTimeout(scrollPageAndReviewsToTop, 150);
    const raf = requestAnimationFrame(scrollPageAndReviewsToTop);

    return () => {
      window.clearTimeout(t1);
      window.clearTimeout(t2);
      window.clearTimeout(t3);
      cancelAnimationFrame(raf);
      document.documentElement.style.overflow = '';
      document.body.style.overflow = '';
      document.body.style.position = '';
      document.body.style.top = '';
      document.body.style.width = '';
      if (isMobile) {
        window.scrollTo(0, 0);
      } else {
        window.scrollTo(0, prevScrollY);
      }
    };
  }, [open, scrollPageAndReviewsToTop]);

  /* ── memoized values ────────────────────────────────── */
  const images = useMemo(() => shop ? [shop.imageUrl, ...(shop.gallery || [])].filter(Boolean) : [], [shop]);

  const portfolioList = useMemo(() => {
    if (!shop?.portfolio || !Array.isArray(shop.portfolio)) return [];
    const seen = new Set();
    return shop.portfolio.filter((item) => {
      const url = typeof item === 'string' ? item : (item?.url || item?.image || item?.imageUrl || '');
      if (!url || seen.has(url)) return false;
      seen.add(url);
      return true;
    }).map((item) => (typeof item === 'string' ? item : (item?.url || item?.image || item?.imageUrl || '')));
  }, [shop]);

  // Portfolio lightbox: Esc to close + lock scroll
  useEffect(() => {
    if (portfolioLightboxIdx === null) return undefined;

    const onKey = (e) => {
      if (e.key === 'Escape') setPortfolioLightboxIdx(null);
      if (e.key === 'ArrowLeft') {
        setPortfolioLightboxIdx((prev) =>
          prev === null ? null : (prev - 1 + portfolioList.length) % portfolioList.length
        );
      }
      if (e.key === 'ArrowRight') {
        setPortfolioLightboxIdx((prev) =>
          prev === null ? null : (prev + 1) % portfolioList.length
        );
      }
    };

    const prevOverflow = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    window.addEventListener('keydown', onKey);

    return () => {
      document.body.style.overflow = prevOverflow;
      window.removeEventListener('keydown', onKey);
    };
  }, [portfolioLightboxIdx, portfolioList.length]);

  const shopReviewMeta = useMemo(() => {
    if (!shop) return { count: 0, reviews: [] };
    const shopId = shop._id || shop.id;
    const name = shop.name || shopName;
    return getFakeReviewsForShop(shopId, name, lang, 8, shop.serviceCategory);
  }, [shop, shopName, lang]);

  const mergedReviews = useMemo(
    () => mergeShopReviews(reviews, shopReviewMeta),
    [reviews, shopReviewMeta],
  );
  
  // Fix availability slots - convert ISO date to weekday name
  const getWeekdayFromDate = useCallback((isoDate) => {
    const date = new Date(isoDate);
    const weekdays = ['Sunday', 'Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday'];
    return weekdays[date.getDay()];
  }, []);
  
  const weekday = useMemo(() => day ? getWeekdayFromDate(day) : '', [day, getWeekdayFromDate]);
  const slots = useMemo(() => shop && Array.isArray(shop.availability?.[weekday]) ? shop.availability[weekday] : [], [shop, weekday]);
  const shopStaffList = useMemo(() => shop ? getStaffList(shop) : [], [shop]);

  // Helper function to get all available dates for the calendar
  const getAllAvailableDates = useCallback((limit = 90) => {
    if (!shop?.availability) return [];
    const availableDates = [];
    const availableWeekdays = Object.keys(shop.availability);
    const today = moment().startOf('day');

    availableWeekdays.forEach(weekday => {
      const weekdayIndex = moment().day(weekday).day();
      let daysUntilTarget = weekdayIndex - today.day();
      if (daysUntilTarget <= 0) daysUntilTarget += 7;
      
      for (let i = 0; i < limit; i++) {
        const date = moment(today).add(daysUntilTarget + (i * 7), 'days');
        availableDates.push(date.format('YYYY-MM-DD'));
      }
    });

    return availableDates;
  }, [shop]);

  /* ── fetch shop ─────────────────────────────────────── */
  useEffect(() => {
    let isMounted = true;
    
    const fetchShop = async (retryCount = 0) => {
      try {
        setLoading(true);
        const { data } = await http.getJson(`/businesses?name=${encodeURIComponent(shopName)}&limit=1`);
        
        if (!isMounted) return;
        
        if (data?.length) {
          setShop(data[0]);
        } else {
          if (retryCount < 2) {
            setTimeout(() => fetchShop(retryCount + 1), 1000);
            return;
          }
          navigate('/');
        }
      } catch (err) {
        if (!isMounted) return;
        
        if (retryCount < 2) {
          setTimeout(() => fetchShop(retryCount + 1), 1000);
          return;
        }
        navigate('/');
      } finally {
        if (isMounted) {
          setLoading(false);
        }
      }
    };
    
    fetchShop();
    
    return () => {
      isMounted = false;
    };
  }, [shopName, navigate]);

  /* ── fetch reviews ─────────────────────────────────── */
  const loadReviews = useCallback(async (providerId, providerName) => {
    if (!providerId && !providerName) return;
    try {
      let reviewsData = [];
      
      // Try providerId first
      if (providerId) {
        try {
          const url = `/reviews?providerId=${providerId}&_t=${Date.now()}`;
          const data = await http.getJson(url);
          reviewsData = Array.isArray(data) ? data : [];
        } catch (err) {
          // Reviews by providerId failed
        }
      }
      
      // If no reviews found by ID, try by name
      if (reviewsData.length === 0 && providerName) {
        try {
          const url = `/reviews?provider=${encodeURIComponent(providerName)}&_t=${Date.now()}`;
          const data = await http.getJson(url);
          reviewsData = Array.isArray(data) ? data : [];
        } catch (err) {
          // Reviews by provider name failed
        }
      }
      setReviews(reviewsData);
    } catch (err) {
      setReviews([]);
    }
  }, []); // No dependencies

  useEffect(() => {
    if (shop?._id || shop?.name) {
      loadReviews(shop?._id, shop?.name);
    }
  }, [shop?._id, shop?.name]); // Remove loadReviews from dependencies

  /* ── fetch booked slots for selected date ──────────── */
  useEffect(() => {
    if (!day || !shop) {
      setBookedSlots([]);
      return;
    }

    const fetchBookedSlots = async () => {
      try {
        console.log('Fetching booked slots for:', { shopName: shop.name, date: day });
        const response = await http.getJson('/api/bookings');
        const allBookings = Array.isArray(response) ? response : response.data || [];
        
        // Filter bookings for this shop and date with active status
        const activeStatuses = ['pending', 'held', 'accepted', 'confirmed', 'rescheduled'];
        const bookedForDate = allBookings.filter(booking => 
          booking.shopName === shop.name && 
          booking.selectedDate === day &&
          activeStatuses.includes(booking.status)
        );
        
        console.log('Booked bookings for this date:', bookedForDate);
        
        // Group by time slot and count staff bookings
        const timeSlotBookings = {};
        bookedForDate.forEach(booking => {
          const time = booking.selectedHour;
          if (!timeSlotBookings[time]) {
            timeSlotBookings[time] = new Set();
          }
          if (booking.staffId) {
            timeSlotBookings[time].add(booking.staffId);
          }
        });
        
        // Get total staff count for this shop
        const totalStaff = shop.staff ? shop.staff.length : 0;
        console.log('Shop staff count:', totalStaff);
        console.log('Shop staff data:', shop.staff);
        console.log('Time slot bookings:', timeSlotBookings);
        
        // Find fully booked time slots (all staff booked)
        const fullyBookedSlots = Object.keys(timeSlotBookings).filter(time => 
          timeSlotBookings[time].size >= totalStaff && totalStaff > 0
        );
        
        console.log('Fully booked slots (all staff booked):', fullyBookedSlots);
        console.log('Setting booked slots state:', fullyBookedSlots);
        setBookedSlots(fullyBookedSlots);
      } catch (err) {
        console.error('Error fetching booked slots:', err);
        setBookedSlots([]);
      }
    };

    fetchBookedSlots();
  }, [day, shop]);

  /* ── save booking intent for non-logged-in users ─────── */

  useEffect(() => {
    if (!isAuthenticated && shop?.name && selectedServices.length > 0 && day && slot) {
      const intent = createBookingIntent(shop, selectedServices, day, slot, staff);
      if (intent) {
        saveBookingIntent(intent);
      }
    }
  }, [isAuthenticated, shop?.name, selectedServices, day, slot, staff?.name]);

  /* ── booking logic ──────────────────────────────────── */
  const handleBooking = async () => {
    if (!shop || selectedServices.length === 0 || !day || !slot) return;

    if (!isAuthenticated) {
      const intent = createBookingIntent(shop, selectedServices, day, slot, staff);
      if (intent) {
        saveBookingIntent(intent);
        navigate('/login', { 
          state: { 
            from: { pathname: `/booking/${encodeURIComponent(shopName)}` },
            bookingIntent: true 
          } 
        });
      }
      return;
    }

    const totalPrice = selectedServices.reduce((sum, s) => sum + toNumber(s.price), 0);
    const bookingData = {
      shopName: shop.name,
      provider: shop._id,
      services: selectedServices.map(s => ({ serviceName: s.serviceName, price: s.price })),
      selectedDate: day,
      selectedHour: slot,
      totalPrice,
      staffName: staff?.name || '',
    };

    try {
      console.log('BookingPage: Submitting booking data:', bookingData);
      console.log('BookingPage: Using endpoint /api/bookings directly');
      const result = await http.postJson('/api/bookings', bookingData);
      console.log('BookingPage: Booking created successfully:', result);
      
      // Verify the booking was actually created
      if (!result || !result._id) {
        console.error('BookingPage: Invalid booking response:', result);
        alert('Booking creation failed. Please try again.');
        return;
      }
      
      // Small delay to ensure booking is fully processed before redirect
      setTimeout(() => {
        console.log('BookingPage: Redirecting to dashboard...');
        navigate('/dashboard');
      }, 500);
    } catch (err) {
      console.error('BookingPage: Booking failed', err);
      console.error('BookingPage: Error details:', {
        status: err.status,
        message: err.message,
        data: err.data,
        response: err.response
      });
      
      // Check if user is unauthorized and redirect to login
      if (err.status === 401 || err.message?.toLowerCase().includes('unauthorized')) {
        console.log('BookingPage: User unauthorized, redirecting to login...');
        navigate('/login');
        return;
      }

      if (err.status === 403 && (err.data?.code === 'EMAIL_NOT_VERIFIED' || /verify your email/i.test(err.message || ''))) {
        const intent = createBookingIntent(shop, selectedServices, day, slot, staff);
        if (intent) saveBookingIntent(intent);
        markVerifyForBooking();
        navigate('/dashboard', { state: { verifyForBooking: true } });
        return;
      }
      
      // Extract user-friendly error message
      let errorMessage = err.message || 'Please try again.';
      
      // If we have structured error data, try to get a better message
      if (err.data) {
        if (err.data.details && (err.data.details.detail || err.data.details.title)) {
          errorMessage = err.data.details.detail || err.data.details.title;
        } else if (err.data.message) {
          errorMessage = err.data.message;
        }
      }
      
      // For 400 Bad Request, provide more context
      if (err.status === 400) {
        errorMessage = errorMessage || 'Invalid request. Please check your booking details and try again.';
      }
      
      alert(`Booking failed: ${errorMessage}`);
    }
  };

  /* ── render helpers ────────────────────────────────── */
  if (loading || authLoading) {
    return (
      <div className="booking-container">
        <div className="loading-container">
          <div className="loading-fallback">
            <div className="loading-spinner"></div>
          </div>
        </div>
      </div>
    );
  }

  if (!shop) {
    return (
      <div className="booking-container">
        <div className="loading-container">
          <div className="loading-fallback">
            <p>Shop not found</p>
          </div>
        </div>
      </div>
    );
  }

  
  // Debug logging (only in development)
  if (process.env.NODE_ENV === 'development') {
    console.log('BookingPage Debug:', {
      day,
      weekday,
      availability: shop.availability,
      slots,
      shopName: shop.name
    });
  }

  /* ── calendar helpers ─────────────────────────────── */
  const monthStart = currentMonth.clone().startOf('month');
  const start = monthStart.clone().startOf('week');
  const cells = Array.from({ length: 42 }, (_, i) => start.clone().add(i, 'd'));
  const availableDates = getAllAvailableDates();
  const todayISO = moment().format('YYYY-MM-DD');

  const goToMonth = (delta) => {
    setCurrentMonth(currentMonth.clone().add(delta, 'months'));
  };

  const handleBookNowClick = () => {
    if (!isAuthenticated) {
      navigate('/register', {
        state: {
          from: { pathname: `/booking/${encodeURIComponent(shopName)}` },
          bookingIntent: true,
        },
      });
      return;
    }
    const targetRef = shopStaffList.length > 0 ? staffRef : serviceRef;
    const element = targetRef?.current;
    if (element) {
      element.scrollIntoView({ behavior: 'smooth', block: 'center', inline: 'nearest' });
    }
  };

  const openCustomerReviews = () => {
    window.scrollTo(0, 0);
    document.documentElement.scrollTop = 0;
    document.body.scrollTop = 0;
    setOpen(true);
  };

  const mapHref =
    shop.mapEmbed && /^https?:\/\//i.test(shop.mapEmbed.trim())
      ? shop.mapEmbed.trim()
      : `https://www.google.com/maps/search/?api=1&query=${encodeURIComponent(`${shop.name} ${shop.town || ''}`)}`;

  const handlePortfolioClick = () => {
    setShowPortfolio(true);
    setTimeout(() => {
      portfolioRef.current?.scrollIntoView({ behavior: 'smooth', block: 'center', inline: 'nearest' });
    }, 100);
  };

  const handleHeroImgError = (e) => {
    if (images.length > 1) {
      const nextIdx = (heroIdx + 1) % images.length;
      e.target.src = images[nextIdx];
      setHeroIdx(nextIdx);
    } else {
      e.target.src = img('/images/placeholder-shop.jpg');
    }
  };

  /* ── render ───────────────────────────────────────── */
  return (
    <div className="booking-container bp2">
      <div className="bp2-main">

      <header className="bp2-top">
        <div
          className="bp2-media"
          onTouchStart={handleTouchStart}
          onTouchEnd={(e) => handleTouchEnd(e, images.length)}
        >
          <img
            src={images[heroIdx]}
            alt={`${shop.name} ${heroIdx + 1}`}
            className="bp2-media-img"
            onError={handleHeroImgError}
          />
          {images.length > 1 && (
            <>
              <button
                type="button"
                className="bp2-media-nav prev"
                onClick={() => setHeroIdx((heroIdx - 1 + images.length) % images.length)}
                aria-label={t('prevImage')}
              >
                ‹
              </button>
              <button
                type="button"
                className="bp2-media-nav next"
                onClick={() => setHeroIdx((heroIdx + 1) % images.length)}
                aria-label={t('nextImage')}
              >
                ›
              </button>
              <div className="bp2-media-count" aria-hidden>
                {heroIdx + 1}/{images.length}
              </div>
            </>
          )}
        </div>

        <div className="bp2-card">
          <div className="bp2-card-main">
            <h1
              className={[
                'bp2-name',
                (shop.name || '').length > 35 ? 'is-xl' : '',
                (shop.name || '').length > 28 && (shop.name || '').length <= 35 ? 'is-long' : '',
              ].filter(Boolean).join(' ')}
              title={shop.name}
            >
              {shop.name}
            </h1>
            <div className="bp2-meta">
              <span className="bp2-town">
                <FaMapMarkerAlt aria-hidden />
                {shop.town}
              </span>
              {shop.rating > 0 && (
                <span className="bp2-stars" aria-label={`Rating ${shop.rating}`}>
                  <Stars n={shop.rating} />
                </span>
              )}
            </div>
          </div>

          <div className="bp2-card-actions">
            <div className="bp2-tools">
              <button type="button" className="bp2-tool" onClick={openCustomerReviews}>
                <FaStar aria-hidden />
                <span>{t('customerReviews')}</span>
              </button>
              <a href={mapHref} target="_blank" rel="noopener noreferrer" className="bp2-tool">
                <FaMap aria-hidden />
                <span>{t('viewMap')}</span>
              </a>
              {shop.phone && (
                <a href={`tel:${shop.phone}`} className="bp2-tool">
                  <FaPhoneAlt aria-hidden />
                  <span>{t('callNow')}</span>
                </a>
              )}
              {portfolioList.length > 0 && (
                <button type="button" className="bp2-tool" onClick={handlePortfolioClick}>
                  <FaImages aria-hidden />
                  <span>{t('portfolio')}</span>
                </button>
              )}
            </div>
            <button type="button" className="bp2-book" onClick={handleBookNowClick}>
              {t('bookNow')}
            </button>
          </div>
        </div>
      </header>

      {/* PORTFOLIO SECTION */}
      {portfolioList.length > 0 && (
        <section className={`bp2-panel bp2-panel--portfolio portfolio-section ${showPortfolio ? 'portfolio-visible' : 'portfolio-hidden'}`}>
          <h2 className="section-title bp2-panel-title" ref={portfolioRef}>{t('portfolio')}</h2>
          <div className={`portfolio-slider-container bp2-portfolio-frame${portfolioLightboxIdx !== null ? ' is-lightbox-open' : ''}`}>
            <div className="portfolio-slider-track">
              {portfolioList.map((imageUrl, index) => (
                <button
                  key={`first-${index}`}
                  type="button"
                  className="portfolio-slide-item"
                  onClick={() => setPortfolioLightboxIdx(index)}
                  aria-label={`${t('tapToView')} ${index + 1}`}
                >
                  <img
                    src={imageUrl}
                    alt={`${t('portfolioPreview')} ${index + 1}`}
                    className="portfolio-slide-image"
                    onError={(e) => {
                      e.target.src = img('/images/placeholder-shop.jpg');
                    }}
                  />
                  <span className="portfolio-tap-btn">{t('tapToView')}</span>
                </button>
              ))}
              {portfolioList.length > 1 && portfolioList.map((imageUrl, index) => (
                <button
                  key={`second-${index}`}
                  type="button"
                  className="portfolio-slide-item"
                  onClick={() => setPortfolioLightboxIdx(index)}
                  aria-label={`${t('tapToView')} ${index + 1}`}
                >
                  <img
                    src={imageUrl}
                    alt={`${t('portfolioPreview')} ${index + 1}`}
                    className="portfolio-slide-image"
                    onError={(e) => {
                      e.target.src = img('/images/placeholder-shop.jpg');
                    }}
                  />
                  <span className="portfolio-tap-btn">{t('tapToView')}</span>
                </button>
              ))}
            </div>
          </div>
        </section>
      )}

      {/* LOGIN PROMPT FOR NON-AUTHENTICATED USERS */}
      {!isAuthenticated && (
        <div className="login-prompt-card">
          <div className="login-prompt-content">
            <h2 className="login-prompt-title">{t('signInToBook')}</h2>
            <p className="login-prompt-description">
              {t('createAccountOrSignIn')}
            </p>
            <div className="login-prompt-actions">
              <button 
                className="login-prompt-btn primary"
                onClick={() => navigate('/login', { 
                  state: { 
                    from: { pathname: `/booking/${encodeURIComponent(shopName)}` },
                    bookingIntent: true 
                  } 
                })}
              >
                {t('signIn')}
              </button>
              <button 
                className="login-prompt-btn secondary"
                onClick={() => navigate('/register', { 
                  state: { 
                    from: { pathname: `/booking/${encodeURIComponent(shopName)}` },
                    bookingIntent: true 
                  } 
                })}
              >
                {t('createAccount')}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* STAFF PICKER */}
      {shopStaffList.length > 0 && isAuthenticated && (
        <section className="bp2-panel bp2-panel--staff">
          <h2 className="section-title bp2-panel-title" ref={staffRef}>{t('chooseStaff')}</h2>
          <div className="staff-list bp2-staff-grid">
            {shopStaffList.map((member, i) => {
              const name = member.name || '';
              const initials = name
                .split(' ')
                .filter(Boolean)
                .map(p => p[0])
                .join('')
                .slice(0, 2)
                .toUpperCase();
              return (
                <div
                  key={i}
                  className={`staff-card ${staff?.name === member.name ? 'selected' : ''}`}
                  onClick={() => {
                    const newStaff = staff?.name === member.name ? null : member;
                    setStaff(newStaff);
                    if (newStaff && serviceRef.current) {
                      setTimeout(() => {
                        const element = serviceRef.current;
                        if (element) {
                          element.scrollIntoView({ behavior: 'smooth', block: 'center', inline: 'nearest' });
                        }
                      }, 300);
                    }
                  }}
                >
                  <div className="staff-avatar"><span className="initials">{initials}</span></div>
                  <div className="staff-name">{name}</div>
                  {staff?.name === member.name && <div className="staff-check">✓</div>}
                </div>
              );
            })}
          </div>
        </section>
      )}

      {/* SERVICE PICKER */}
      {isAuthenticated && (
        <section className="bp2-panel bp2-panel--services">
          <h2 className="section-title bp2-panel-title" ref={serviceRef}>{t('selectServicesAsMuchAsYouNeed')}</h2>
          <div className="booking-services-section">
            <div className="services-list bp2-services-list">
            {(() => {
              const allServices = Array.isArray(shop.services) ? shop.services : [];
              const visibleServices =
                isMobileServices && !showAllServices
                  ? allServices.slice(0, 5)
                  : allServices;
              return visibleServices.map((s, i) => {
                const isSelected = selectedServices.some(x => x.serviceName === s.serviceName);
                return (
                  <div
                    key={`${s.serviceName}-${i}`}
                    className={`service-slot ${isSelected ? 'selected' : ''}`}
                    onClick={() => {
                      if (isSelected) {
                        setSelectedServices(prev => prev.filter(x => x.serviceName !== s.serviceName));
                      } else {
                        setSelectedServices(prev => [...prev, { serviceName: s.serviceName, price: s.price }]);
                      }
                      setTimeout(() => {
                        const element = dateRef.current;
                        if (element) {
                          element.scrollIntoView({ behavior: 'smooth', block: 'center', inline: 'nearest' });
                        }
                      }, 300);
                    }}
                  >
                    <span className="service-name">{s.serviceName}</span>
                    <span className="service-price-wrapper">
                      <span className="service-price">{s.price}</span>
                      <span className="service-currency"> EGP</span>
                    </span>
                    {isSelected && <span className="service-check" aria-hidden>✓</span>}
                  </div>
                );
              });
            })()}
            </div>
            {isMobileServices && Array.isArray(shop.services) && shop.services.length > 5 && (
              <button
                type="button"
                className="bp2-services-more"
                onClick={() => setShowAllServices((prev) => !prev)}
              >
                {showAllServices ? t('showLess') : t('showMore')}
              </button>
            )}
          </div>
        </section>
      )}

      {/* DATE + TIME */}
      {selectedServices.length > 0 && isAuthenticated && (
        <div className="bp2-datetime">
          <section className="bp2-panel bp2-panel--date booking-calendar-section" ref={dateRef}>
            <h2 className="section-title bp2-panel-title">{t('selectDate')}</h2>
            <div className="booking-calendar bp2-calendar">
              <header className="booking-cal-header">
                <button type="button" onClick={() => goToMonth(-1)} className="cal-nav-btn">‹</button>
                <h3 className="cal-month-title">{`${MONTHS[currentMonth.month()]} ${currentMonth.year()}`}</h3>
                <button type="button" onClick={() => goToMonth(1)} className="cal-nav-btn">›</button>
              </header>
              <div className="cal-weekdays">
                {WEEKDAYS.map(d => <span key={d}>{d}</span>)}
              </div>
              <div className="cal-grid">
                {cells.map(d => {
                  const iso = d.format('YYYY-MM-DD');
                  const otherMon = d.month() !== currentMonth.month();
                  const isToday = iso === todayISO;
                  const isAvailable = availableDates.includes(iso);
                  const isSelected = day === iso;
                  const isPast = d.isBefore(moment(), 'day');

                  return (
                    <button
                      key={iso}
                      type="button"
                      className={['cal-day', otherMon && 'cal-other', isPast && 'cal-past', isAvailable && 'cal-available', isSelected && 'cal-selected', isToday && 'cal-today'].filter(Boolean).join(' ')}
                      onClick={() => {
                        if (isAvailable && !isPast) {
                          const newDay = day === iso ? '' : iso;
                          setDay(newDay);
                          setSlot('');
                          if (newDay) {
                            setTimeout(() => {
                              timeRef.current?.scrollIntoView({
                                behavior: 'smooth',
                                block: 'center',
                                inline: 'nearest',
                              });
                            }, 300);
                          }
                        }
                      }}
                      disabled={!isAvailable || isPast || otherMon}
                      title={otherMon ? '' : isPast ? 'Past date' : isAvailable ? 'Click to select' : 'Not available'}
                    >
                      <span className="cal-day-num">{d.date()}</span>
                    </button>
                  );
                })}
              </div>
            </div>
          </section>

          <section className="bp2-panel bp2-panel--time" ref={timeRef}>
            <h2 className="section-title bp2-panel-title">{t('selectTime')}</h2>
            {day ? (
              <div className="hours-scroll bp2-time-grid">
                {slots.length > 0 ? (
                  slots.map((hour, i) => {
                    const isBooked = bookedSlots.includes(hour);
                    return (
                      <button
                        key={i}
                        type="button"
                        className={`hour-slot ${slot === hour ? 'selected' : ''} ${isBooked ? 'booked' : ''}`}
                        onClick={() => {
                          if (!isBooked) {
                            const newSlot = slot === hour ? '' : hour;
                            setSlot(newSlot);
                            if (newSlot) {
                              setTimeout(() => {
                                receiptRef.current?.scrollIntoView({
                                  behavior: 'smooth',
                                  block: 'center',
                                  inline: 'nearest',
                                });
                              }, 300);
                            }
                          }
                        }}
                        disabled={isBooked}
                        title={isBooked ? t('bookedSlotTitle') : t('availableSlotTitle')}
                      >
                        {hour}
                      </button>
                    );
                  })
                ) : (
                  <div className="no-slots"></div>
                )}
              </div>
            ) : (
              <div className="bp2-time-placeholder">
                {lang === 'ar' ? 'اختر تاريخاً أولاً لعرض الأوقات' : 'Pick a date first to see available times'}
              </div>
            )}
          </section>
        </div>
      )}

      {/* RECEIPT / CART */}
      {(selectedServices.length > 0 || day || slot) && isAuthenticated && (
        <aside className="receipt-card bp2-panel bp2-panel--receipt" ref={receiptRef}>
          <div className="receipt-head">
            <span className="bp2-receipt-label">{lang === 'ar' ? 'ملخص الحجز' : 'Booking summary'}</span>
            <span className="shop-inline">{shop.name}</span>
          </div>
          <div className="receipt-body">
            {selectedServices.length > 0 && (
              <div>
                <dt>{t('serviceLabel')}</dt>
                <dd>
                  {selectedServices.map((s, i) => (
                    <span key={i} className="receipt-service-line">
                      {s.serviceName} — {s.price} EGP
                    </span>
                  ))}
                </dd>
              </div>
            )}
            {day && (
              <div>
                <dt>{t('dateLabel')}</dt>
                <dd>{moment(day).format('MMM DD, YYYY')}</dd>
              </div>
            )}
            {slot && (
              <div>
                <dt>{t('timeLabel')}</dt>
                <dd>{slot}</dd>
              </div>
            )}
            {staff && (
              <div>
                <dt>{t('staffLabel')}</dt>
                <dd>{staff.name}</dd>
              </div>
            )}
            {selectedServices.length > 0 && (
              <div className="total-row">
                <dt>{t('totalLabel')}</dt>
                <dd>{selectedServices.reduce((sum, s) => sum + toNumber(s.price), 0)} EGP</dd>
              </div>
            )}
          </div>
          <div className="receipt-cta">
            <button
              type="button"
              className="cta-book"
              onClick={handleBooking}
              disabled={selectedServices.length === 0 || !day || !slot}
            >
              {t('bookAppointment')}
            </button>
          </div>
        </aside>
      )}

      </div>{/* /.bp2-main */}

      {/* REVIEWS MODAL — portaled to body so position:fixed is viewport-relative */}
      {open &&
        createPortal(
          <div
            className="reviews-overlay"
            ref={reviewsOverlayRef}
            onClick={() => setOpen(false)}
          >
            <div
              className="reviews-modal"
              ref={reviewsModalRef}
              onClick={(e) => e.stopPropagation()}
            >
              <button type="button" className="close-btn" onClick={() => setOpen(false)}>
                ×
              </button>
              <h2>{t('customerReviews')}</h2>
              {mergedReviews.reviews.length > 0 ? (
                mergedReviews.reviews.map((review, i) => {
                  const user = review.userName || 'Anonymous';
                  const initials = user
                    .split(' ')
                    .filter(Boolean)
                    .map((p) => p[0])
                    .join('')
                    .slice(0, 2)
                    .toUpperCase();
                  return (
                    <div key={i} className="review-item">
                      <div className="review-head">
                        <div className="review-userbox">
                          <div className="review-avatar">{initials}</div>
                          <strong className="review-user">{user}</strong>
                        </div>
                        <div className="review-stars">
                          <Stars n={review.rating} />
                        </div>
                      </div>
                      {review.comment ? (
                        <div className="review-text">{review.comment}</div>
                      ) : null}
                    </div>
                  );
                })
              ) : (
                <p>{t('noReviews')}</p>
              )}
            </div>
          </div>,
          document.body
        )}

      {/* PORTFOLIO LIGHTBOX */}
      {portfolioLightboxIdx !== null &&
        portfolioList[portfolioLightboxIdx] &&
        createPortal(
          <div
            className="portfolio-lightbox"
            role="dialog"
            aria-modal="true"
            aria-label={t('portfolio')}
            onClick={() => setPortfolioLightboxIdx(null)}
          >
            <button
              type="button"
              className="close-btn portfolio-lightbox-close"
              onClick={() => setPortfolioLightboxIdx(null)}
              aria-label={t('close')}
            >
              ×
            </button>
            {portfolioList.length > 1 && (
              <button
                type="button"
                className="portfolio-lightbox-nav prev"
                onClick={(e) => {
                  e.stopPropagation();
                  setPortfolioLightboxIdx((prev) =>
                    (prev - 1 + portfolioList.length) % portfolioList.length
                  );
                }}
                aria-label="Previous"
              >
                ‹
              </button>
            )}
            <img
              src={portfolioList[portfolioLightboxIdx]}
              alt={`${t('portfolioPreview')} ${portfolioLightboxIdx + 1}`}
              className="portfolio-lightbox-image"
              onClick={(e) => e.stopPropagation()}
              onError={(e) => {
                e.target.src = img('/images/placeholder-shop.jpg');
              }}
            />
            {portfolioList.length > 1 && (
              <button
                type="button"
                className="portfolio-lightbox-nav next"
                onClick={(e) => {
                  e.stopPropagation();
                  setPortfolioLightboxIdx((prev) => (prev + 1) % portfolioList.length);
                }}
                aria-label="Next"
              >
                ›
              </button>
            )}
            <span className="portfolio-lightbox-count">
              {portfolioLightboxIdx + 1} / {portfolioList.length}
            </span>
          </div>,
          document.body
        )}

    </div>
  );
}